'use client';

import { useEffect, useState, useTransition } from 'react';
import { useClerk, useSignIn as useClerkSignIn, useSignUp as useClerkSignUp } from '@clerk/nextjs';
import { isClerkAPIResponseError } from '@clerk/nextjs/errors';
import { useSessionStorage } from '@uidotdev/usehooks';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { usePasswordlessSignUpVariant } from '@/app/(auth)/_hooks/use-passwordless-sign-up-variant';
import { useSignUpQ1Variant } from '@/app/(auth)/_hooks/use-sign-up-q1-variant';
import { persistLastAuthenticationStrategy } from '@/app/(auth)/_utils/auth-last-authentication-strategy';
import { existingAccountSignInHref } from '@/app/(auth)/_utils/existing-account-handoff';
import { resetStaleOAuthState } from '@/app/(auth)/_utils/reset-stale-oauth-state';
import { completeSignUp } from '@/app/(auth)/sign-up/_utils/complete-sign-up';
import { portfolioClaimSignUpMetadata } from '@/features/portfolio/_utils/portfolio-claim-origin';
import { resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { SIGNUP_ACCOUNT_CREATION_METHOD_KEY, SIGNUP_NEWSLETTER_KEY, SIGNUP_PASSWORD_CHOSEN_KEY } from '@/infra/constants/routes';
import { baseFetch } from '@/infra/http/base';
import { getAnonymousCorrelationId } from '@/lib/analytics/get-anonymous-correlation-id';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { ErrorTracking } from '@/lib/error-tracking';
import { useGetFeatureFlag } from '@/lib/feature-flags/hooks/use-get-feature-flag';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { resolvePostSignUpRedirectUrl } from '@/utils/resolve-post-sign-up-redirect-url';
import type { SignUpStep } from './use-sign-up-flow-store';
import { useSignUpFlowStore } from './use-sign-up-flow-store';
const RESEND_COOLDOWN_SECONDS = 60;
/** Clerk's code for "that address already has an account". */
const CLERK_IDENTIFIER_EXISTS_CODE = 'form_identifier_exists';
type SignUpAccountCreationMethod = 'email' | 'google';
function getClerkApiResponseError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return null;
  }
  return isClerkAPIResponseError(error) ? error : null;
}
function getReturnedClerkErrorCode(error: unknown, clerkResponseError: ReturnType<typeof getClerkApiResponseError>): string | undefined {
  const fromApi = clerkResponseError?.errors[0]?.code;
  if (fromApi) {
    return fromApi;
  }
  if (error && typeof error === 'object' && 'code' in error && typeof (error as {
    code: unknown;
  }).code === 'string') {
    return (error as {
      code: string;
    }).code;
  }
  return;
}
function getSignUpPasswordErrorDescription(error: unknown, clerkResponseError: ReturnType<typeof getClerkApiResponseError>): string {
  if (clerkResponseError) {
    return clerkResponseError.errors[0]?.longMessage || clerkResponseError.errors[0]?.message || 'An unexpected error occurred';
  }
  if (error && typeof error === 'object') {
    const rec = error as {
      longMessage?: string;
      message?: string;
    };
    return rec.longMessage || rec.message || 'An unexpected error occurred';
  }
  return 'An unexpected error occurred';
}
interface SignUpFormData {
  emailAddress: string;
  firstName: string;
  lastName: string;
  newsletter?: boolean;
  // Absent in the passwordless arm unless the reader chose "Create a password instead".
  password?: string;
}
interface FinalizeNavContext {
  decorateUrl: (url: string) => string;
  session: {
    getToken: () => Promise<string | null>;
    reload: () => Promise<unknown>;
    currentTask?: unknown;
    user?: {
      id?: string;
    } | null;
  };
}
export function useSignUp(): {
  step: SignUpStep;
  isPending: boolean;
  isRedirecting: boolean;
  isGoogleLoading: boolean;
  onSignUpSubmit: (data: SignUpFormData) => void;
  onSignUpWithGoogle: (newsletter?: boolean) => Promise<void>;
  onSignUpSubmitCode: (code: string) => void;
  onResendCode: () => Promise<void>;
  isResendDisabled: boolean;
  timeLeft: number;
  shouldRedirectToStart: boolean;
  emailAddress: string | undefined;
  goToStep: (step: SignUpStep) => void;
} {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    campaign,
    content,
    medium,
    source,
    referrer,
    partner,
    portfolioCode,
    portfolioUrl,
    clickIdFallbacks
  } = useUtmParams();
  const portfolioAttribution = resolvePortfolioAttribution({
    portfolioCode,
    portfolioUrl,
    utmContent: content,
    referrer: null
  });
  const clerk = useClerk();
  const {
    signUp
  } = useClerkSignUp();
  const {
    signIn,
    fetchStatus: signInFetchStatus
  } = useClerkSignIn();
  const {
    trackSignUpStarted,
    trackSignUpEmailCodeSent,
    trackSignUpEmailVerified,
    trackSignUpEmailCodeResent,
    trackSignUpExistingAccountHandoff,
    trackSsoSignUpCompleted,
    trackAuthError,
    identifyAuthUser
  } = useAuthEvents();
  const freemiumVariant = useGetFeatureFlag('ab_test_freemium');
  const {
    variant: signUpQ1Variant
  } = useSignUpQ1Variant();
  const {
    variant: passwordlessVariant
  } = usePasswordlessSignUpVariant();
  const {
    track
  } = useAnalytics();
  const [isPending, startTransition] = useTransition();
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [newsletter, setNewsletter] = useSessionStorage<boolean | null>(SIGNUP_NEWSLETTER_KEY, null);
  const [accountCreationMethod, setAccountCreationMethod] = useSessionStorage<SignUpAccountCreationMethod | null>(SIGNUP_ACCOUNT_CREATION_METHOD_KEY, null);
  // A Google sign-up the callback carried to the code screen fails there as a Google one, not an email one.
  const verificationMethod = accountCreationMethod ?? 'email';

  // Kept in session storage, like the two above, so it survives the reload the code screen can take.
  // Clerk's sign-up resource types a `hasPassword` field, but the runtime leaves it undefined.
  const [passwordChosen, setPasswordChosen] = useSessionStorage<boolean | null>(SIGNUP_PASSWORD_CHOSEN_KEY, null);
  const step = useSignUpFlowStore(s => s.step);
  const startFormDraftEmailAddress = useSignUpFlowStore(s_0 => s_0.startFormDraft.emailAddress);
  const isRedirecting = useSignUpFlowStore(s_1 => s_1.isRedirecting);
  const shouldRedirectToStart = !isRedirecting && (signUp?.verifications?.emailAddress?.status === null || signUp?.verifications?.emailAddress?.status === 'expired');
  function goToStep(newStep: SignUpStep): void {
    useSignUpFlowStore.getState().onChange({
      step: newStep
    });
  }
  async function trackUnverified(data: SignUpFormData): Promise<void> {
    try {
      await baseFetch.post('me/unverified', {
        json: {
          email: data.emailAddress || '',
          anonymous_id: getAnonymousCorrelationId()
        }
      });
    } catch {
      return;
    }
  }
  function reportSignUpStartError(error: unknown): void {
    const clerkCatchError = getClerkApiResponseError(error);
    if (clerkCatchError) {
      trackAuthError('sign_up', 'start', clerkCatchError.errors[0]?.code, {
        method: 'email'
      });
      const message = clerkCatchError.errors[0]?.longMessage || clerkCatchError.errors[0]?.message || 'An unexpected error occurred';
      toast.error('Something went wrong', {
        description: message,
        position: 'bottom-center'
      });
      return;
    }
    trackAuthError('sign_up', 'start', 'sign_up_start_unhandled_error', {
      method: 'email',
      error_message: error instanceof Error ? error.message : String(error)
    });
    ErrorTracking.captureMessage('sign_up_start_unhandled_error', {
      tags: {
        auth_flow: 'sign_up',
        auth_step: 'start',
        error_code: 'sign_up_start_unhandled_error'
      },
      extra: {
        error_message: error instanceof Error ? error.message : String(error)
      }
    });
    toast.error('Something went wrong', {
      description: 'An unexpected error occurred',
      position: 'bottom-center'
    });
  }
  function reportSignUpVerificationError(error_0: unknown): {
    handled: boolean;
  } {
    const clerkVerificationError = getClerkApiResponseError(error_0);
    if (clerkVerificationError) {
      trackAuthError('sign_up', 'email_verification', clerkVerificationError.errors[0]?.code, {
        method: verificationMethod
      });
      const message_0 = clerkVerificationError.errors[0]?.longMessage || clerkVerificationError.errors[0]?.message || 'An unexpected error occurred';
      toast.error('Something went wrong', {
        description: message_0,
        position: 'bottom-center'
      });
      return {
        handled: true
      };
    }
    trackAuthError('sign_up', 'email_verification', 'email_verification_unhandled_error', {
      method: verificationMethod,
      error_message: error_0 instanceof Error ? error_0.message : String(error_0)
    });
    ErrorTracking.captureMessage('email_verification_unhandled_error', {
      tags: {
        auth_flow: 'sign_up',
        auth_step: 'email_verification',
        error_code: 'email_verification_unhandled_error'
      },
      extra: {
        error_message: error_0 instanceof Error ? error_0.message : String(error_0)
      }
    });
    toast.error('Something went wrong', {
      description: 'Unable to finish sign up right now. Please try again.',
      position: 'bottom-center'
    });
    return {
      handled: false
    };
  }
  function reportSignUpGoogleError(error_1: unknown): void {
    const clerkCatchError_0 = getClerkApiResponseError(error_1);
    if (clerkCatchError_0) {
      trackAuthError('sign_up', 'start', clerkCatchError_0.errors[0]?.code, {
        method: 'google'
      });
      const message_1 = clerkCatchError_0.errors[0]?.longMessage || clerkCatchError_0.errors[0]?.message || 'An unexpected error occurred';
      toast.error('Something went wrong', {
        description: message_1,
        position: 'bottom-center'
      });
      return;
    }
    trackAuthError('sign_up', 'start', 'sign_up_google_sso_unhandled_error', {
      method: 'google',
      error_message: error_1 instanceof Error ? error_1.message : String(error_1)
    });
    ErrorTracking.captureMessage('sign_up_google_sso_unhandled_error', {
      tags: {
        auth_flow: 'sign_up',
        auth_step: 'start',
        error_code: 'sign_up_google_sso_unhandled_error'
      },
      extra: {
        error_message: error_1 instanceof Error ? error_1.message : String(error_1)
      }
    });
    toast.error('Something went wrong', {
      description: 'An unexpected error occurred',
      position: 'bottom-center'
    });
  }

  // Defaulted to false for the same reason as the store: a caller that omits the argument must
  // not be able to opt the user in on their behalf.
  async function onSignUpWithGoogle(newsletter_0 = false): Promise<void> {
    if (!signIn) {
      return;
    }
    try {
      trackSignUpStarted('google', newsletter_0);
      setNewsletter(newsletter_0);
      setAccountCreationMethod('google');
      const {
        error: resetError
      } = await resetStaleOAuthState({
        signIn,
        signUp,
        clerkClient: clerk.client
      });
      if (resetError) {
        trackAuthError('sign_up', 'start', resetError.code, {
          method: 'google'
        });
        toast.error('Something went wrong', {
          description: resetError.longMessage || resetError.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
        return;
      }
      const redirectUrl = resolvePostSignUpRedirectUrl(searchParams);
      const allowlisted = getAllowlistedSearchParams(searchParams, clickIdFallbacks);
      const {
        error: error_3
      } = await signIn.sso({
        redirectUrl,
        strategy: 'oauth_google',
        redirectCallbackUrl: allowlisted ? `/sign-up/sso-callback?${allowlisted}` : '/sign-up/sso-callback'
      });
      if (error_3) {
        await resetStaleOAuthState({
          signIn,
          signUp,
          clerkClient: clerk.client
        });
        trackAuthError('sign_up', 'start', error_3.code, {
          method: 'google'
        });
        toast.error('Something went wrong', {
          description: error_3.longMessage || error_3.message || 'An unexpected error occurred',
          position: 'bottom-center'
        });
      }
    } catch (error_2) {
      await resetStaleOAuthState({
        signIn,
        signUp,
        clerkClient: clerk.client
      });
      reportSignUpGoogleError(error_2);
    }
  }
  function onSignUpSubmit(data_0: SignUpFormData): void {
    if (!signUp) {
      return;
    }
    startTransition(async () => {
      trackUnverified(data_0).catch(() => undefined);
      trackSignUpStarted('email', data_0.newsletter, Boolean(data_0.password));
      try {
        const signUpParams = {
          firstName: data_0.firstName,
          lastName: data_0.lastName,
          emailAddress: data_0.emailAddress,
          unsafeMetadata: {
            utm_campaign: campaign,
            utm_medium: medium,
            utm_source: source,
            utm_referrer: referrer,
            ...(content ? {
              utm_content: content
            } : {}),
            search_params: getAllowlistedSearchParams(searchParams, clickIdFallbacks),
            newsletter: data_0.newsletter,
            account_creation_method: 'email',
            ...(partner ? {
              partner
            } : {}),
            ...(portfolioAttribution ? {
              portfolio_attribution: portfolioAttribution
            } : {}),
            // Also here, not only in the provision payload: `buildFallbackPayload` rebuilds that
            // payload off Clerk when `localStorage` is gone, which is the very hop — verify on the
            // phone, finish on the laptop — this marker exists to survive.
            ...portfolioClaimSignUpMetadata(content),
            ...(freemiumVariant === undefined ? {} : {
              ab_test_freemium: freemiumVariant
            }),
            ...(signUpQ1Variant === undefined ? {} : {
              ab_test_sign_up_2026_q1: signUpQ1Variant
            }),
            ...(passwordlessVariant === undefined ? {} : {
              ab_test_passwordless_sign_up_2026_q3: passwordlessVariant
            })
          }
        };

        // No password means the passwordless arm: the account is created from name + email alone and the
        // email code below is the whole credential. Needs "Sign-up with password" OFF on the Clerk
        // instance, otherwise this comes back with `password` in `missingFields` and never completes.
        const {
          error: error_5
        } = data_0.password ? await signUp.password({
          ...signUpParams,
          password: data_0.password
        }) : await signUp.create(signUpParams);
        if (error_5) {
          const clerkResponseError = getClerkApiResponseError(error_5);
          const errorCode = getReturnedClerkErrorCode(error_5, clerkResponseError);
          trackAuthError('sign_up', 'start', errorCode, {
            method: 'email'
          });

          // Not a failure to explain, a person on the wrong page: carry them to sign-in with the address
          // they just typed, rather than a toast telling them the address is taken.
          if (errorCode === CLERK_IDENTIFIER_EXISTS_CODE) {
            trackSignUpExistingAccountHandoff();
            router.push(existingAccountSignInHref(data_0.emailAddress, getAllowlistedSearchParams(searchParams, clickIdFallbacks)));
            return;
          }
          const message_2 = getSignUpPasswordErrorDescription(error_5, clerkResponseError);
          toast.error('Something went wrong', {
            description: message_2,
            position: 'bottom-center'
          });
          return;
        }
        setNewsletter(data_0.newsletter ?? null);
        setAccountCreationMethod('email');
        setPasswordChosen(Boolean(data_0.password));
        await signUp.verifications.sendEmailCode();
        trackSignUpEmailCodeSent();
        goToStep('email-verification');
      } catch (error_4) {
        reportSignUpStartError(error_4);
      }
    });
  }
  async function completeVerifiedSignUp(verifiedSignUp: NonNullable<ReturnType<typeof useClerkSignUp>['signUp']>): Promise<void> {
    const allowlistedParams = getAllowlistedSearchParams(searchParams, clickIdFallbacks);
    const finalizeNavHolder: {
      current: FinalizeNavContext | null;
    } = {
      current: null
    };
    await verifiedSignUp.finalize({
      navigate: ({
        session,
        decorateUrl
      }) => {
        if (session?.currentTask) {
          return;
        }
        finalizeNavHolder.current = {
          session,
          decorateUrl
        };
      }
    });
    if (!finalizeNavHolder.current) {
      useSignUpFlowStore.getState().onChange({
        isRedirecting: false
      });
      return;
    }
    await completeSignUp({
      userId: verifiedSignUp.createdUserId ?? '',
      email: verifiedSignUp.emailAddress ?? useSignUpFlowStore.getState().startFormDraft.emailAddress ?? '',
      firstName: verifiedSignUp.firstName ?? '',
      lastName: verifiedSignUp.lastName ?? '',
      newsletter: newsletter ?? false,
      accountCreationMethod: accountCreationMethod ?? 'email',
      allowlistedParams,
      campaign,
      medium,
      source,
      referrer,
      partner,
      portfolioAttribution,
      utmContent: content,
      signUpQ1Variant,
      tracking: {
        identifyAuthUser,
        track,
        onCompleted: () => {
          // A Google sign-up the callback carried here because Clerk still wanted the address confirmed.
          // It is a Google account: counting it as an email one would inflate the email-only event and
          // point the "last used" badge at a password that does not exist.
          if (accountCreationMethod === 'google') {
            trackSsoSignUpCompleted();
            persistLastAuthenticationStrategy('oauth_google');
            return;
          }
          trackSignUpEmailVerified();
          // Drives the "last used" badge on sign-in, which has to name a method this account can use.
          persistLastAuthenticationStrategy(passwordChosen === false ? 'email_code' : 'password');
        }
      },
      clearStoredNewsletter: () => setNewsletter(null),
      clearStoredAccountCreationMethod: () => {
        setAccountCreationMethod(null);
        setPasswordChosen(null);
      },
      redirect: () => {
        router.push(resolvePostSignUpRedirectUrl(searchParams));
      }
    });
  }
  function onSignUpSubmitCode(code: string): void {
    if (!signUp) {
      return;
    }
    startTransition(async () => {
      const signUpFlowStore = useSignUpFlowStore.getState();
      signUpFlowStore.onChange({
        isRedirecting: true
      });
      try {
        const {
          error: error_7
        } = await signUp.verifications.verifyEmailCode({
          code
        });
        if (error_7) {
          signUpFlowStore.onChange({
            isRedirecting: false
          });
          trackAuthError('sign_up', 'email_verification', error_7.code, {
            method: verificationMethod
          });
          toast.error('Something went wrong', {
            description: error_7.longMessage || error_7.message || 'Invalid or expired verification code.',
            position: 'bottom-center'
          });
          return;
        }
        if (signUp.status === 'complete' && signUp.createdSessionId) {
          await completeVerifiedSignUp(signUp);
          return;
        }
        signUpFlowStore.onChange({
          isRedirecting: false
        });
      } catch (error_6) {
        signUpFlowStore.onChange({
          isRedirecting: false
        });
        const {
          handled
        } = reportSignUpVerificationError(error_6);
        if (handled) {
          return;
        }
        const allowlistedParams_0 = getAllowlistedSearchParams(searchParams, clickIdFallbacks);
        const fallbackUrl = allowlistedParams_0 ? `/sign-up?${allowlistedParams_0}` : '/sign-up';
        router.push(fallbackUrl);
      }
    });
  }
  async function onResendCode(): Promise<void> {
    if (!signUp) {
      return;
    }
    trackSignUpEmailCodeResent();
    setIsResendDisabled(true);
    setTimeLeft(RESEND_COOLDOWN_SECONDS);
    try {
      await signUp.verifications.sendEmailCode();
    } catch (error_8) {
      const clerkVerificationError_0 = getClerkApiResponseError(error_8);
      trackAuthError('sign_up', 'email_verification', clerkVerificationError_0?.errors[0]?.code, {
        method: verificationMethod
      });
      const message_3 = clerkVerificationError_0?.errors[0]?.longMessage || clerkVerificationError_0?.errors[0]?.message || 'An unexpected error occurred';
      toast.error('Something went wrong', {
        description: message_3,
        position: 'bottom-center'
      });
      setIsResendDisabled(false);
      setTimeLeft(0);
    }
  }
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsResendDisabled(false);
      return;
    }
    const timer = setTimeout(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);
  return {
    step,
    isPending,
    isRedirecting,
    isGoogleLoading: signInFetchStatus === 'fetching',
    onSignUpSubmit,
    onSignUpWithGoogle,
    onSignUpSubmitCode,
    onResendCode,
    isResendDisabled,
    timeLeft,
    shouldRedirectToStart,
    emailAddress: signUp?.emailAddress ?? startFormDraftEmailAddress ?? undefined,
    goToStep
  };
}